import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

// ---------------------------------------------------------------------------
// Gasto de IA (seguridad, tanda 6). La llave de Anthropic del portal la pagan todos
// los clientes juntos y el cupo del plan cuenta ANÁLISIS, no dinero (el Copiloto no
// descuenta). Aquí los tokens que registra el proxy (ia_llamadas) se convierten en un
// gasto ESTIMADO en US$ y se avisa cuando el mes se acerca al presupuesto o cuando un
// cliente gasta en un día más que el tope de alerta. Es aviso, no freno: el freno duro
// es el límite de gasto en la consola de Anthropic.
// SQL: docs/corredata/control-plane/schema-ia-gasto.sql
// ---------------------------------------------------------------------------

const usd = (n) => 'US$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CAMPOS = [
  { k: 'presupuesto_mes_usd',  label: 'Presupuesto del mes (US$)', ayuda: 'Gasto de IA esperado, todos los clientes juntos' },
  { k: 'alerta_pct',           label: 'Avisar al llegar al (%)',   ayuda: 'Del presupuesto del mes' },
  { k: 'tope_cliente_dia_usd', label: 'Tope por cliente al día (US$)', ayuda: 'Más que esto en un día = uso anómalo' },
  { k: 'precio_entrada_mtok',  label: 'Precio entrada (US$ / millón de tokens)', ayuda: 'Según el modelo del proxy' },
  { k: 'precio_salida_mtok',   label: 'Precio salida (US$ / millón de tokens)',  ayuda: 'Según el modelo del proxy' },
];

export default function GastoIA({ clientes }) {
  const [cfg, setCfg] = useState(null);
  const [filas, setFilas] = useState([]);
  const [err, setErr] = useState('');
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [abierto, setAbierto] = useState(false);

  const load = useCallback(async () => {
    const [c, r] = await Promise.all([
      supabase.from('ia_gasto_config').select('*').eq('id', 1).maybeSingle(),
      supabase.rpc('ia_gasto_resumen'),
    ]);
    if (c.error || r.error) { setErr((c.error || r.error).message); return; }
    setErr('');
    setCfg(c.data);
    setFilas(r.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="card" style={{ padding: '12px 16px', marginBottom: 22, fontSize: 13, color: 'var(--warning)', background: 'var(--warning-bg)' }}>
        Gasto de IA: no se pudo leer ({err}). Si es la primera vez, falta correr <b>schema-ia-gasto.sql</b> en corredata-control.
      </div>
    );
  }
  if (!cfg) return null;

  // Caché de Anthropic (manual del Copiloto, TTL 1 h): escribir = 2× la entrada, leer = 0.1×.
  const costo = (tin, tout, cEsc = 0, cLei = 0) => {
    const pin = Number(cfg.precio_entrada_mtok), pout = Number(cfg.precio_salida_mtok);
    return (Number(tin) * pin + Number(tout) * pout + Number(cEsc || 0) * pin * 2 + Number(cLei || 0) * pin * 0.1) / 1e6;
  };
  const nombre = (id) => clientes.find(c => c.id === id)?.nombre || `Cliente ${id}`;
  const porCliente = filas.map(f => ({
    ...f, nombre: nombre(f.cliente_id),
    mes: costo(f.tokens_in_mes, f.tokens_out_mes, f.cache_esc_mes, f.cache_lei_mes),
    hoy: costo(f.tokens_in_hoy, f.tokens_out_hoy, f.cache_esc_hoy, f.cache_lei_hoy),
  })).sort((a, b) => b.mes - a.mes);

  const totalMes = porCliente.reduce((s, f) => s + f.mes, 0);
  const totalHoy = porCliente.reduce((s, f) => s + f.hoy, 0);
  const presupuesto = Number(cfg.presupuesto_mes_usd) || 0;
  const pct = presupuesto > 0 ? (totalMes / presupuesto) * 100 : 0;
  // Proyección lineal al cierre del mes (hora de Panamá).
  const ahora = new Date(Date.now() - 5 * 3600 * 1000);
  const diaMes = ahora.getUTCDate();
  const diasMes = new Date(Date.UTC(ahora.getUTCFullYear(), ahora.getUTCMonth() + 1, 0)).getUTCDate();
  const proyeccion = diaMes > 0 ? (totalMes / diaMes) * diasMes : totalMes;

  const alertas = [];
  if (presupuesto > 0 && pct >= 100) alertas.push({ nivel: 'danger', txt: `El gasto de IA del mes (${usd(totalMes)}) ya pasó el presupuesto de ${usd(presupuesto)}.` });
  else if (presupuesto > 0 && pct >= Number(cfg.alerta_pct)) alertas.push({ nivel: 'warning', txt: `El gasto de IA del mes va en ${usd(totalMes)}: ${Math.round(pct)} % del presupuesto de ${usd(presupuesto)}.` });
  else if (presupuesto > 0 && proyeccion > presupuesto && diaMes >= 5) alertas.push({ nivel: 'warning', txt: `A este ritmo el mes cerraría en ${usd(proyeccion)}, por encima del presupuesto de ${usd(presupuesto)}.` });
  porCliente.filter(f => f.hoy > Number(cfg.tope_cliente_dia_usd)).forEach(f =>
    alertas.push({ nivel: 'danger', txt: `${f.nombre} lleva ${usd(f.hoy)} de IA hoy (${f.llamadas_hoy} llamadas), más que el tope de alerta de ${usd(cfg.tope_cliente_dia_usd)}. Revisa si es uso normal.` }));

  const color = pct >= 100 ? 'var(--danger)' : pct >= Number(cfg.alerta_pct) ? 'var(--warning)' : 'var(--success)';

  const guardar = async () => {
    const payload = {};
    for (const { k, label } of CAMPOS) {
      const v = Number(editando[k]);
      if (!Number.isFinite(v) || v < 0) { window.alert(`"${label}" debe ser un número de 0 en adelante.`); return; }
      payload[k] = v;
    }
    setGuardando(true);
    const { error } = await supabase.from('ia_gasto_config').update({ ...payload, actualizado_en: new Date().toISOString() }).eq('id', 1);
    setGuardando(false);
    if (error) { window.alert('No se pudo guardar: ' + error.message); return; }
    setEditando(null);
    load();
  };

  return (
    <div style={{ marginBottom: 22 }}>
      {alertas.map((a, i) => (
        <div key={i} className="card" style={{ padding: '10px 14px', marginBottom: 8, fontSize: 13, fontWeight: 600, color: `var(--${a.nivel})`, background: `var(--${a.nivel}-bg)`, borderColor: `var(--${a.nivel})` }}>
          ⚠ {a.txt}
        </div>
      ))}

      <div className="card" style={{ padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: .4 }}>Gasto de IA este mes (estimado)</div>
            <div style={{ fontSize: 22, fontWeight: 900, color }}>
              {usd(totalMes)} <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted)' }}>de {usd(presupuesto)} · hoy {usd(totalHoy)} · proyección {usd(proyeccion)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" onClick={() => setAbierto(v => !v)}>{abierto ? 'Ocultar detalle' : 'Por cliente'}</button>
            <button className="btn btn-sm" onClick={() => setEditando({ ...cfg })}>Configurar</button>
          </div>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: 'var(--surface-2)', marginTop: 10, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color }} />
        </div>

        {abierto && (
          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            {porCliente.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--faint)' }}>Ninguna llamada a la IA por el servicio central este mes.</div>
            ) : (
              <table>
                <thead><tr><th>Cliente</th><th>Llamadas (mes)</th><th>Gasto mes</th><th>Llamadas hoy</th><th>Gasto hoy</th></tr></thead>
                <tbody>
                  {porCliente.map(f => (
                    <tr key={f.cliente_id}>
                      <td style={{ fontWeight: 600 }}>{f.nombre}</td>
                      <td>{f.llamadas_mes}</td>
                      <td>{usd(f.mes)}</td>
                      <td>{f.llamadas_hoy}</td>
                      <td style={{ color: f.hoy > Number(cfg.tope_cliente_dia_usd) ? 'var(--danger)' : undefined, fontWeight: f.hoy > Number(cfg.tope_cliente_dia_usd) ? 700 : undefined }}>{usd(f.hoy)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 8 }}>
              Estimado con los tokens que registra el servicio central y los precios de "Configurar". No incluye lo que una instancia gaste con su propia llave (respaldo si el portal se cae). El cobro real y el freno duro están en la consola de Anthropic.
            </div>
          </div>
        )}
      </div>

      {editando && (
        <div className="modal-overlay" onClick={() => setEditando(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--line)', fontSize: 17, fontWeight: 700 }}>Alertas de gasto de IA</div>
            <div style={{ padding: 22, display: 'grid', gap: 12 }}>
              {CAMPOS.map(({ k, label, ayuda }) => (
                <div key={k}>
                  <label className="lbl">{label}</label>
                  <input className="input" type="number" min="0" step="any" value={editando[k] ?? ''} onChange={e => setEditando(s => ({ ...s, [k]: e.target.value }))} />
                  <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 3 }}>{ayuda}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn" onClick={() => setEditando(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
